import {
  faCircleInfo,
  faRobot,
  faUserShield,
  faHeartPulse,
  faClipboardCheck,
  faTriangleExclamation,
  faScaleBalanced,
  faRotateLeft,
  faEnvelope,
  faUserTie,
  faShieldHalved,
  faPhone,
  faLocationDot,
} from '@fortawesome/free-solid-svg-icons';
import { LegalDocument } from './types';

export const AI_HEALTH_TERMS: LegalDocument = {
  slug: 'ai-health-terms',
  route: '/ai-health-terms',
  title: 'AI & Health Insights Terms',
  subtitle:
    'The extra terms for our automated features — what they read, what they cannot do, and the limits on both sides.',
  version: '1.0',
  effectiveDate: 'October 23, 2026',
  lastUpdated: 'October 23, 2026',
  icon: faRobot,
  contact: {
    title: 'Questions & Grievances',
    note:
      'For questions about the AI Features, or to raise a grievance about something an automated feature produced, contact us using the details below. We encourage you to raise the matter with us first so we can attempt to resolve it directly.',
    items: [
      { icon: faEnvelope, label: 'Support email', value: 'support@infinicare.com', href: 'mailto:support@infinicare.com' },
      { icon: faUserTie, label: 'Data Protection Officer', value: 'dpo@infinicare.com', href: 'mailto:dpo@infinicare.com' },
      { icon: faShieldHalved, label: 'Privacy requests', value: 'privacy@infinicare.com', href: 'mailto:privacy@infinicare.com' },
      { icon: faPhone, label: 'Phone', value: '+91-90350 52403' },
      {
        icon: faLocationDot,
        label: 'Registered address',
        value: 'No. 31, 3rd Floor, 09th Main Road, 6th Sector, HSR Layout, Bangalore - 560102, Karnataka, India',
      },
    ],
  },
  sections: [
    {
      id: 'section-1',
      number: '1',
      title: 'Applicability',
      icon: faCircleInfo,
      iconClass: 'lg-icon-navy',
      blocks: [
        {
          type: 'p',
          text: 'These AI & Health Insights Terms ("AI Terms") govern your use of the automated features listed in Section 2 (the "AI Features"). They apply in addition to the Infinicare Terms of Use (the "Terms") and are incorporated into the Terms by reference.',
        },
        {
          type: 'p',
          text: 'Capitalised terms that are not defined in these AI Terms have the meaning given to them in the Terms.',
        },
        {
          type: 'p',
          text: 'You may choose not to agree to these AI Terms. If you do not agree, the AI Features will not be available to you. Declining these AI Terms does not affect the rest of the Platform, which continues to be governed by the Terms and the Privacy Policy.',
        },
        {
          type: 'p',
          text: 'Where these AI Terms conflict with the Terms, these AI Terms govern in respect of the AI Features, except for Section 8 (Limitation of Liability) of the Terms, which continues to apply as described in Section 7 below.',
        },
        {
          type: 'note',
          text: 'Important: the AI Features provide automated, indicative information. They are not a doctor, and they are not a substitute for professional medical advice, diagnosis or treatment.',
        },
      ],
    },
    {
      id: 'section-2',
      number: '2',
      title: 'The AI Features',
      icon: faRobot,
      iconClass: 'lg-icon-cyan',
      blocks: [
        {
          type: 'p',
          text: 'The "AI Features" are the automated features on the Infinicare Platform that generate information, assessments or suggestions without a doctor reviewing the output. As at the effective date of these AI Terms, they are:',
        },
        {
          type: 'ul',
          items: [
            'AI Family Doctor — the chat assistant, including when it reads documents you upload.',
            'Health Assessment — the questionnaire and the report it generates.',
            'Health Insights — the AI-generated observations shown with your Health Summary.',
            'Disease Prediction — the risk views produced by our rule-based, machine-learning and AI models.',
          ],
        },
        {
          type: 'p',
          text: 'The AI Features work on the health information you provide directly, or that is generated from your records, reports and connected devices ("User Information"). This can include your health records, vitals, laboratory results, prescriptions, the symptoms and conditions you log, your assessment answers, the reports and prescriptions you upload, and summaries received from wearable devices you have connected.',
        },
        {
          type: 'p',
          text: 'The AI Features do not include teleconsultation with a doctor. A teleconsultation is provided by a registered medical practitioner and is governed by its own terms and consent.',
        },
        {
          type: 'p',
          text: 'Which AI Features are available to you may depend on your plan, or on the services your employer has enabled. A feature that is not enabled for you is not part of the AI Features you are using.',
        },
        {
          type: 'p',
          text: 'We may add, change or withdraw an AI Feature over time. Section 8 explains how we tell you about changes to these AI Terms.',
        },
      ],
    },
    {
      id: 'section-3',
      number: '3',
      title: 'Your Information, Consent and Withdrawal',
      icon: faUserShield,
      iconClass: 'lg-icon-blue',
      blocks: [
        {
          type: 'p',
          text: 'We read your health records for the AI Features only with your specific consent. Consent for the AI Features is separate from your acceptance of the Terms and the Privacy Policy, and is requested the first time you use an AI Feature.',
        },
        {
          type: 'p',
          text: 'When you give that consent, the AI Features can read:',
        },
        {
          type: 'ul',
          items: [
            'Your age, gender and blood group.',
            'Your vitals, laboratory results, conditions, allergies, medicines and vaccinations.',
            'Your symptoms, assessment results and wearable summaries.',
            'Anything you type, say or upload while using an AI Feature.',
          ],
        },
        {
          type: 'p',
          text: 'The AI Features are not given your name, email address, phone number, employee or member identifier or company, the names of your doctors and hospitals, your addresses, or your insurance and payment details.',
        },
        {
          type: 'p',
          text: 'Your consent stays in force until you withdraw it. You may withdraw it at any time from inside AI Family Doctor. Withdrawing stops the AI Features reading your records and deletes the consultations stored for you, and those parts of the AI Features then stop working. Withdrawal does not affect processing carried out before it.',
        },
        {
          type: 'p',
          text: 'We do not use your health information to train third-party AI models.',
        },
        {
          type: 'p',
          text: 'Our collection, use, sharing and retention of personal data is described in the Privacy Policy, which applies to these AI Terms.',
        },
      ],
    },
    {
      id: 'section-4',
      number: '4',
      title: 'Automated Output Is Not Medical Advice',
      icon: faHeartPulse,
      iconClass: 'lg-icon-rose',
      blocks: [
        {
          type: 'p',
          text: 'The output of the AI Features is indicative and automated. It is generated from the User Information available to the feature, using algorithms and a medical knowledge base derived from external sources.',
        },
        {
          type: 'p',
          text: 'The AI Features do not diagnose a medical condition, do not prescribe or advise how you should treat one, and do not replace a consultation with a doctor. A diagnosis or a treatment decision can only be made by a qualified medical professional who has examined you.',
        },
        {
          type: 'note',
          text: 'Not a substitute: always seek the advice of your physician or another qualified health provider with any questions about a medical condition. Never disregard professional medical advice, or delay seeking it, because of something an AI Feature showed you.',
        },
        {
          type: 'p',
          text: 'In an emergency, contact emergency services immediately — dial 112 for the national emergency helpline or 108 for an ambulance. Do not use the AI Features as a substitute for emergency care.',
        },
      ],
    },
    {
      id: 'section-5',
      number: '5',
      title: 'Your Responsibility for Accurate Information',
      icon: faClipboardCheck,
      iconClass: 'lg-icon-green',
      blocks: [
        {
          type: 'p',
          text: 'The AI Features work only on the User Information they can read. If information is missing, out of date or incorrect, the result is likely to be wrong.',
        },
        {
          type: 'p',
          text: 'You are responsible for the accuracy of what you enter, upload and connect, and for keeping your records current — including changes to your medicines, allergies, conditions and test results.',
        },
        {
          type: 'p',
          text: 'You should not rely on an AI Feature to detect or rule out a serious condition. If you feel unwell, or a result does not match how you feel, speak to a doctor.',
        },
      ],
    },
    {
      id: 'section-6',
      number: '6',
      title: 'Disclaimer of Warranties',
      icon: faTriangleExclamation,
      iconClass: 'lg-icon-orange',
      blocks: [
        {
          type: 'p',
          text: 'To the maximum extent permitted by applicable law, the AI Features are provided "as is" and "as available", and we make no representation, warranty, undertaking or guarantee of any kind, express or implied, in relation to them or to their use.',
        },
        {
          type: 'p',
          text: 'Without limiting the above, and to the same extent, we disclaim any representation or warranty:',
        },
        {
          type: 'ul',
          items: [
            'That output will be accurate, complete or reliable.',
            'That the AI Features are fit or suitable for any purpose, whether or not we know of that purpose, have reason to know of it, or have been advised of it.',
            'Of non-infringement, or of condition of title.',
            'That operation will be uninterrupted, timely, error-free, or free of bugs or viruses.',
            'That any defect will be corrected.',
          ],
        },
        {
          type: 'p',
          text: 'No advice or information obtained through an AI Feature creates a warranty or representation that is not expressly stated in these AI Terms.',
        },
      ],
    },
    {
      id: 'section-7',
      number: '7',
      title: 'Limitation of Liability',
      icon: faScaleBalanced,
      iconClass: 'lg-icon-slate',
      blocks: [
        {
          type: 'p',
          text: 'Liability in connection with the AI Features is governed by Section 8 (Limitation of Liability) of the Terms, which applies to these AI Terms. If any provision of these AI Terms is read as creating a different liability standard, Section 8 of the Terms prevails.',
        },
        {
          type: 'p',
          text: 'Subject to that section, and to the maximum extent permitted by applicable law, neither Infinicare nor its affiliates, officers, directors, employees or agents are liable to you or to anyone else for any direct, indirect, consequential, special or punitive loss or damage, for personal injury, mental anguish or emotional distress, or for any other personal loss (whether in contract, tort or otherwise) arising from access to, use of, or inability to access or use the AI Features.',
        },
        {
          type: 'p',
          text: 'If you are dissatisfied with an AI Feature, or with these AI Terms, your remedy is to stop using the AI Feature.',
        },
      ],
    },
    {
      id: 'section-8',
      number: '8',
      title: 'Changes, Governing Law and Contact',
      icon: faRotateLeft,
      iconClass: 'lg-icon-purple',
      blocks: [
        {
          type: 'p',
          text: 'We may update these AI Terms as the AI Features change or as the law requires. The current version, its version number and the date it was last updated are published on this page.',
        },
        {
          type: 'p',
          text: 'Where a change materially affects how the AI Features work, or what you are consenting to, we will ask you to accept the updated AI Terms before you continue using those features.',
        },
        {
          type: 'p',
          text: 'These AI Terms are governed by the laws of India. Disputes are subject to the governing law and dispute-resolution provisions of the Terms.',
        },
        {
          type: 'p',
          text: 'Questions, concerns or complaints about the AI Features or these AI Terms can be sent to the contacts below.',
        },
      ],
    },
  ],
  related: [
    { label: 'Terms of Use', route: '/terms' },
    { label: 'Privacy Policy', route: '/privacy-policy' },
    { label: 'Service Policies', route: '/customer-policies' },
  ],
};
